/** Prefix every OAuth `returnTo` must carry. It is the API endpoint that resumes the flow. */
const AUTHORIZE_PREFIX = '/api/oauth/authorize?';

/** Whitespace and control characters. Browsers strip them before they parse a URL. */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\u0000-\u0020\u007f]/;

/**
 * Accepts a `returnTo` value only when it is a same-origin path.
 * Anything with a scheme, a protocol-relative prefix, a backslash or a control character
 * is refused, so the value can never send the browser to another site.
 */
export function sanitizeReturnTo(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }

    // A missing leading slash, a protocol-relative prefix or a backslash can escape the origin.
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
        return null;
    }

    if (UNSAFE_CHARACTERS.test(value)) {
        return null;
    }

    return value;
}

/**
 * Accepts a `returnTo` value only when it is the same-origin OAuth authorize endpoint.
 * The consent page uses this. Any other path means the link was tampered with.
 */
export function sanitizeAuthorizeReturnTo(value: string | null | undefined): string | null {
    const path = sanitizeReturnTo(value);

    return path !== null && path.startsWith(AUTHORIZE_PREFIX) ? path : null;
}

/**
 * Tells whether a sanitized `returnTo` points at the API instead of a client route.
 * An API path needs a full page navigation, because the server answers with a redirect.
 */
export function isApiReturnTo(value: string): boolean {
    return value.startsWith('/api/');
}
